import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';

function QueryScreen() {
  const { dispatch } = useApp();
  const [query, setQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'system',
      content: '🧠 Welcome to your Brainstorming Agent! Ask me about team skills, mission planning, project dependencies, or strategic development insights. I have access to your team data, mission information, and company documents.',
      timestamp: new Date().toLocaleTimeString(),
      isWelcome: true
    }
  ]);
  const chatEndRef = useRef(null);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Generate session ID on component mount
  useEffect(() => {
    const generateSessionId = () => {
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 9);
      return `brainstorm_${timestamp}_${randomId}`;
    };
    
    setSessionId(generateSessionId());
  }, []);

  // Format AI response for better readability
  const formatAIResponse = (text) => {
    if (!text) return text;
    
    // Split into lines and process
    const lines = text.split('\n');
    const formattedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Skip empty lines but preserve spacing
      if (line.trim() === '') {
        formattedLines.push('');
        continue;
      }
      
      // Handle headers (lines that start with ** and end with **)
      if (line.match(/^\*\*.*\*\*:?\s*$/)) {
        line = line.replace(/^\*\*(.*?)\*\*:?\s*$/, '🎯 **$1**');
      }
      
      // Handle bullet points
      if (line.match(/^\s*\*\s+/)) {
        line = line.replace(/^\s*\*\s+/, '• ');
      }
      
      // Handle numbered lists - keep as is
      
      // Handle sub-bullets with indentation
      if (line.match(/^\s{4,}\*/)) {
        line = line.replace(/^\s+\*\s+/, '  ◦ ');
      }
      
      formattedLines.push(line);
    }
    
    return formattedLines.join('\n');
  };




  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Store the query before clearing it
    const currentQuery = query;
    
    // Add user message to chat history
    const userMessage = {
      type: 'user',
      content: currentQuery,
      timestamp: new Date().toLocaleTimeString()
    };
    
    // Add loading message
    const loadingMessage = {
      type: 'ai',
      content: 'Analyzing your query and gathering context...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setChatHistory(prev => [...prev, userMessage, loadingMessage]);
    setQuery(''); // Clear input immediately


    try {
      // Brainstorming API request
      const requestBody = {
        query: currentQuery,
        session_id: sessionId
      };

      console.log('=== BRAINSTORMING API REQUEST ===');
      console.log('URL:', 'https://langraphdev-development.up.railway.app/api/hey-mission/brainstorm');
      console.log('Method:', 'POST');
      console.log('Headers:', {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      });
      console.log('Request Body:', JSON.stringify(requestBody, null, 2));
      console.log('Session ID:', sessionId);
      console.log('Query:', currentQuery);

      // Make the actual API call to Brainstorming system
      const response = await fetch('https://langraphdev-development.up.railway.app/api/hey-mission/brainstorm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors', // Explicitly set CORS mode
        body: JSON.stringify(requestBody)
      });

      console.log('=== BRAINSTORMING API RESPONSE ===');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('OK:', response.ok);
      console.log('Headers:', Object.fromEntries(response.headers));

      // Get response as text first to see what we're getting
      const responseText = await response.text();
      console.log('Raw Response Text:', responseText);
      console.log('Response Length:', responseText.length);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log('Parsed Response Data:', JSON.stringify(responseData, null, 2));
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.log('Failed to parse response text:', responseText.substring(0, 1000));
        
        // Check if it's an HTML error page
        if (responseText.includes('<!DOCTYPE') || responseText.includes('<html>')) {
          responseData = { 
            success: false,
            error: 'Server returned HTML instead of JSON - likely a CORS or server error',
            rawResponse: responseText.substring(0, 500)
          };
        } else {
          responseData = { 
            success: false,
            error: 'Invalid response format - not valid JSON', 
            rawResponse: responseText.substring(0, 500) + '...',
            parseError: parseError.message
          };
        }
      }

      console.log('=== RESPONSE VALIDATION ===');
      console.log('response.ok:', response.ok);
      console.log('responseData exists:', !!responseData);
      console.log('responseData.success:', responseData?.success);
      console.log('responseData.response:', !!responseData?.response);
      console.log('responseData.error:', responseData?.error);

      if (response.ok && responseData && (responseData.success || responseData.response)) {
        
        console.log('=== SUCCESS RESPONSE PROCESSING ===');
        
        // Extract brainstorming response data
        const aiResponse = responseData.response;
        const sessionInfo = responseData.session_id;
        const contextAnalysis = responseData.context_analysis;
        const contextUsed = responseData.context_used;
        const latency = responseData.total_latency_ms;
        const metadata = responseData.metadata;
        
        console.log('AI Response:', aiResponse);
        console.log('Session Info:', sessionInfo);
        console.log('Context Analysis:', contextAnalysis);
        console.log('Context Used:', contextUsed);
        console.log('Latency:', latency);
        console.log('Metadata:', metadata);

        // Format the AI response for better readability
        const formattedResponse = formatAIResponse(aiResponse);

        // Remove loading message and add AI response
        setChatHistory(prev => {
          const newHistory = prev.filter(msg => !msg.isLoading);
          
          // Create AI response message
          const brainstormResponse = {
            type: 'ai',
            content: formattedResponse,
            timestamp: new Date().toLocaleTimeString(),
            isSuccess: true,
            isFormatted: true,
            metadata: {
              sessionId: sessionInfo,
              contextAnalysis,
              contextUsed,
              latency: latency ? Math.round(latency) : null,
              model: metadata?.llm_model_used?.split('/')[1] || metadata?.llm_model_used,
              contextSources: metadata?.context_sources
            }
          };
          
          return [...newHistory, brainstormResponse];
        });
      } else {
        console.log('=== ERROR RESPONSE PROCESSING ===');
        const errorMsg = responseData?.error || responseData?.message || `HTTP ${response.status}: ${response.statusText}`;
        console.log('Error Message:', errorMsg);
        console.log('Full Response Data for Error:', responseData);
        
        // Remove loading message and add error response
        setChatHistory(prev => {
          const newHistory = prev.filter(msg => !msg.isLoading);
          
          const errorResponse = {
            type: 'ai',
            content: `❌ Query failed: ${errorMsg}`,
            timestamp: new Date().toLocaleTimeString(),
            isError: true
          };
          
          return [...newHistory, errorResponse];
        });
      }

    } catch (error) {
      console.log('=== NETWORK ERROR ===');
      console.error('Network Error:', error);
      console.log('Error Message:', error.message);
      console.log('Error Stack:', error.stack);
      
      // Remove loading message and add error response
      setChatHistory(prev => {
        const newHistory = prev.filter(msg => !msg.isLoading);
        
        const errorResponse = {
          type: 'ai',
          content: `❌ Network error: ${error.message}`,
          timestamp: new Date().toLocaleTimeString(),
          isError: true
        };
        
        return [...newHistory, errorResponse];
      });
    }

    setIsSubmitting(false);
  };

  const goBackToDashboard = () => {
    dispatch({ type: ActionTypes.SET_SCREEN, payload: 'dashboard' });
  };

  const handleSyncData = async () => {
    setIsSyncing(true);
    
    // Add sync message to chat
    const syncMessage = {
      type: 'system',
      content: '🔄 Syncing Neo4j data cache...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setChatHistory(prev => [...prev, syncMessage]);

    try {
      console.log('=== NEO4J SYNC REQUEST ===');
      console.log('URL:', 'https://langraphdev-development.up.railway.app/api/hey-mission/brainstorm/neo4j/sync');
      
      const response = await fetch('https://langraphdev-development.up.railway.app/api/hey-mission/brainstorm/neo4j/sync', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
        mode: 'cors'
            });

      console.log('=== NEO4J SYNC RESPONSE ===');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('OK:', response.ok);

            const responseText = await response.text();
      console.log('Sync Response Text:', responseText);

      let syncResult;
            try {
        syncResult = JSON.parse(responseText);
        console.log('Parsed Sync Result:', syncResult);
            } catch (parseError) {
        console.error('Sync JSON Parse Error:', parseError);
        syncResult = { error: 'Invalid sync response format', rawResponse: responseText };
      }

      // Remove loading message and add result
      setChatHistory(prev => {
        const newHistory = prev.filter(msg => !msg.isLoading || msg.content !== '🔄 Syncing Neo4j data cache...');
        
        const resultMessage = {
          type: 'system',
          content: response.ok 
            ? '✅ Neo4j data cache synchronized successfully! The brainstorming AI now has the latest team and mission data.'
            : `❌ Sync failed: ${syncResult?.error || response.statusText}`,
          timestamp: new Date().toLocaleTimeString(),
          isSuccess: response.ok,
          isError: !response.ok
        };
        
        return [...newHistory, resultMessage];
      });

          } catch (error) {
      console.error('Sync Network Error:', error);
      
      // Remove loading message and add error
      setChatHistory(prev => {
        const newHistory = prev.filter(msg => !msg.isLoading || msg.content !== '🔄 Syncing Neo4j data cache...');
        
        const errorMessage = {
          type: 'system',
          content: `❌ Sync network error: ${error.message}`,
          timestamp: new Date().toLocaleTimeString(),
          isError: true
        };
        
        return [...newHistory, errorMessage];
      });
          } finally {
      setIsSyncing(false);
    }
  };



  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-4xl mx-auto h-[80vh] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-white">Brainstorming Agent</h1>
        <button 
          onClick={goBackToDashboard}
          className="text-sm text-gray-400 hover:text-white"
        >
          ← Back to Dashboard
        </button>
            </div>

      {/* Session Info */}
      {sessionId && (
        <div className="mb-4 p-3 bg-gray-900/30 rounded-lg border border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              <span className="font-medium">Session:</span> {sessionId.split('_').pop()}
                      </div>
            <div className="flex items-center gap-3">
                  <button
                onClick={handleSyncData}
                disabled={isSyncing}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded disabled:opacity-50 flex items-center gap-1"
              >
                {isSyncing ? (
                  <>
                    <div className="spinner w-3 h-3"></div>
                    Syncing...
                      </>
                    ) : (
                      <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    Sync Data
                      </>
                    )}
                  </button>
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-gray-400 hover:text-white"
            >
                New Session
              </button>
            </div>
                </div>
              </div>
            )}

      {/* Chat Area */}
      <div className="flex-1 bg-gray-900/50 rounded-lg p-4 overflow-y-auto mb-4 space-y-3">
        {chatHistory.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-3 ${
              message.type === 'user' 
                ? 'bg-gray-700 text-white border border-gray-600' 
                : message.type === 'system'
                ? message.isError
                  ? 'bg-red-900/50 text-red-200 border border-red-500'
                  : message.isSuccess
                  ? 'bg-gray-600 text-white border border-gray-500'
                  : message.isWelcome
                  ? 'bg-black text-gray-200 border border-gray-600'
                  : 'bg-gray-700 text-gray-200 border border-gray-600'
                : message.isError
                ? 'bg-red-900/50 text-red-200 border border-red-500'
                : message.isSuccess
                ? 'bg-gray-600 text-white border border-gray-500'
                : 'bg-gray-700 text-gray-200 border border-gray-600'
            }`}>
              <div className="text-sm">
                {message.isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner w-4 h-4"></div>
                    {message.content}
              </div>
                ) : (
                  <>
                    {message.isFormatted ? (
                      <div className="whitespace-pre-wrap">
                        {message.content.split('\n').map((line, lineIndex) => {
                          if (line.trim() === '') {
                            return <br key={lineIndex} />;
                          }
                          
                          // Handle headers with emoji
                          if (line.includes('🎯 **') && line.includes('**')) {
                            return (
                              <div key={lineIndex} className="font-semibold text-blue-300 mt-3 mb-2">
                                {line.replace(/🎯 \*\*(.*?)\*\*/, '🎯 $1')}
              </div>
                            );
                          }
                          
                          // Handle bullet points
                          if (line.startsWith('• ')) {
                            return (
                              <div key={lineIndex} className="ml-2 mb-1">
                                {line}
          </div>
        );
                          }
                          
                          // Handle sub-bullets
                          if (line.startsWith('  ◦ ')) {
                            return (
                              <div key={lineIndex} className="ml-6 mb-1 text-gray-300">
                                {line}
          </div>
        );
      }

                          // Handle bold text
                          if (line.includes('**')) {
                            const parts = line.split(/(\*\*.*?\*\*)/);
                            return (
                              <div key={lineIndex} className="mb-1">
                                {parts.map((part, partIndex) => {
                                  if (part.startsWith('**') && part.endsWith('**')) {
                                    return (
                                      <strong key={partIndex} className="font-semibold">
                                        {part.slice(2, -2)}
                                      </strong>
                                    );
                                  }
                                  return part;
                                })}
        </div>
      );
    }

                          // Regular line
                          return (
                            <div key={lineIndex} className="mb-1">
                              {line}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      message.content
                    )}
                    
                    {/* Show metadata for successful AI responses */}
                    {message.metadata && (
                      <div className="mt-3 pt-3 border-t border-gray-600/50 space-y-2">
                        {/* Context Analysis */}
                        {message.metadata.contextAnalysis && (
                          <div className="bg-gray-800/50 p-3 rounded border border-gray-600/30">
                            <div className="text-xs font-semibold text-gray-300 mb-2">🧠 Context Analysis</div>
                            <div className="text-xs text-gray-400">
                              <strong>Reasoning:</strong> {message.metadata.contextAnalysis.context_reasoning}
                            </div>
                            <div className="mt-1 flex gap-3 text-xs">
                              {message.metadata.contextAnalysis.needs_person_data && (
                                <span className="text-blue-300">👥 Person Data</span>
                              )}
                              {message.metadata.contextAnalysis.needs_mission_data && (
                                <span className="text-green-300">🎯 Mission Data</span>
                              )}
                              {message.metadata.contextAnalysis.needs_company_documents && (
                                <span className="text-yellow-300">📄 Company Docs</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Context Used */}
                        {message.metadata.contextUsed && (
                          <div className="bg-gray-800/50 p-3 rounded border border-gray-600/30">
                            <div className="text-xs font-semibold text-gray-300 mb-2">📊 Context Used</div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                              {message.metadata.contextUsed.person_data && (
                                <>
                                  <div><strong>Persons:</strong> {message.metadata.contextUsed.person_data.total_persons}</div>
                                  <div><strong>Missions:</strong> {message.metadata.contextUsed.person_data.total_missions}</div>
                                  <div><strong>Cache:</strong> {message.metadata.contextUsed.person_data.cache_status}</div>
                                  <div><strong>Fetch Time:</strong> {message.metadata.contextUsed.person_data.fetch_time_ms}ms</div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* General Metadata */}
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                          {message.metadata.latency && (
                            <div><strong>Response Time:</strong> {message.metadata.latency}ms</div>
                          )}
                          {message.metadata.model && (
                            <div><strong>Model:</strong> {message.metadata.model}</div>
                          )}
                          {message.metadata.contextSources && (
                            <div><strong>Sources:</strong> {message.metadata.contextSources.join(', ')}</div>
                          )}
                          <div><strong>Type:</strong> Brainstorming</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="text-xs opacity-70 mt-1">
                {message.timestamp}
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      
      {/* Input Area */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask about team skills, mission planning, project dependencies, or strategic insights..."
          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-3 text-sm"
          disabled={isSubmitting}
        />
        <button
          type="submit"
          disabled={isSubmitting || !query.trim()}
          className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 flex items-center gap-2 border border-gray-600"
        >
          {isSubmitting ? (
            <>
              <div className="spinner w-4 h-4"></div>
              Send
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Send
            </>
          )}
        </button>
      </form>
      
      {/* Example Prompts */}
      <div className="mt-3 text-xs text-gray-500">
        <p className="mb-1">💡 <strong>Example brainstorming prompts:</strong></p>
        <div className="space-y-1 text-gray-400">
          <p>• "What team members have the strongest C++ skills for game engine development?"</p>
          <p>• "Break down the Character Animation System mission into smaller, manageable tasks"</p>
          <p>• "Which team members work well together based on their behavioral profiles?"</p>
          <p>• "What missions are currently blocked and what dependencies exist?"</p>
          <p>• "Based on our game design, what core systems should we prioritize next?"</p>
        </div>
        </div>
    </div>
  );
}

export default QueryScreen;
